import { createSelector } from 'reselect';

import { RequestState } from '../reducers/utils/state-manager';

export type ApplicationStatusSubscriptionState = {
  applicationStatusSubscriptionsById: {
    [applicationStatusSubscriptionId: number]: ApplicationStatusSubscription;
  };
  applicationStatusSubscriptionsStateById: {
    [applicationStatusSubscriptionId: number]: RequestState;
  };
  applicationStatusSubscriptionsStateByJobId: { [jobId: number]: RequestState };
  applicationStatusSubscriptionsStateByOrgId: { [orgId: number]: RequestState };
};
type State = { applicationStatusSubscriptions: ApplicationStatusSubscriptionState };

const applicationStatusSubscriptionsByIdSelector = (state: State) =>
  state.applicationStatusSubscriptions.applicationStatusSubscriptionsById;

export const applicationStatusSubscriptionsForIdSelector = createSelector(
  [
    applicationStatusSubscriptionsByIdSelector,
    (_state: State, applicationStatusSubscriptionId: number) => applicationStatusSubscriptionId,
  ],
  (applicationStatusSubscriptionsById, applicationStatusSubscriptionId) =>
    applicationStatusSubscriptionsById[applicationStatusSubscriptionId],
);

const applicationStatusSubscriptionsStateByIdSelector = (state: State) =>
  state.applicationStatusSubscriptions.applicationStatusSubscriptionsStateById;
export const applicationStatusSubscriptionsStateForIdSelector = createSelector(
  [applicationStatusSubscriptionsStateByIdSelector, (_state: State, id: number) => id],
  (applicationStatusSubscriptionsStateById, id) => applicationStatusSubscriptionsStateById[id],
);

const applicationStatusSubscriptionsSelector = createSelector(
  [applicationStatusSubscriptionsByIdSelector],
  (applicationStatusSubscriptionsById) => Object.values(applicationStatusSubscriptionsById),
);
export const applicationStatusSubscriptionsForJobIdSelector = createSelector(
  [applicationStatusSubscriptionsSelector, (_state: State, jobId: string | number) => jobId],
  (applicationStatusSubscriptions: ApplicationStatusSubscription[], jobId: number | string) =>
    applicationStatusSubscriptions.filter((sub) => sub.jobId === jobId),
);
export const applicationStatusSubscriptionsForOrgIdSelector = createSelector(
  [applicationStatusSubscriptionsSelector, (_state: State, orgId: string | number) => orgId],
  (applicationStatusSubscriptions: ApplicationStatusSubscription[], orgId: number | string) =>
    applicationStatusSubscriptions.filter((sub) => sub.orgId === orgId),
);

const createUsersAndEmailsMap = (applicationStatusSubscriptions: ApplicationStatusSubscription[]) =>
  applicationStatusSubscriptions.reduce(
    (acc, curr) => {
      if (typeof curr.subscriberUsername === 'string') {
        acc.users[curr.subscriberUsername] = (acc.users[curr.subscriberUsername] || []).concat([
          curr.applicationStatus,
        ]);
      } else {
        acc.customEmails[curr.otherEmail] = (acc.customEmails[curr.otherEmail] || []).concat([
          curr.applicationStatus,
        ]);
      }
      return acc;
    },
    { users: {}, customEmails: {} } as {
      users: { [username: string]: Array<ApplicationStatus> };
      customEmails: { [username: string]: Array<ApplicationStatus> };
    },
  );

export const applicationStatusSubscriptionsSubscriberMapForJobIdSelector = createSelector(
  [applicationStatusSubscriptionsForJobIdSelector],
  (applicationStatusSubscriptions: ApplicationStatusSubscription[]) =>
    createUsersAndEmailsMap(applicationStatusSubscriptions),
);

export const applicationStatusSubscriptionsSubscriberMapForOrgIdSelector = createSelector(
  [applicationStatusSubscriptionsForOrgIdSelector],
  (applicationStatusSubscriptions: ApplicationStatusSubscription[]) =>
    createUsersAndEmailsMap(applicationStatusSubscriptions),
);

const applicationStatusSubscriptionsStateByJobIdSelector = (state: State) =>
  state.applicationStatusSubscriptions.applicationStatusSubscriptionsStateByJobId;
export const applicationStatusSubscriptionsStateForJobIdSelector = createSelector(
  [applicationStatusSubscriptionsStateByJobIdSelector, (_state: State, jobId: number) => jobId],
  (applicationStatusSubscriptionsStateByJobId, jobId) =>
    applicationStatusSubscriptionsStateByJobId[jobId],
);

const applicationStatusSubscriptionsStateByOrgIdSelector = (state: State) =>
  state.applicationStatusSubscriptions.applicationStatusSubscriptionsStateByOrgId;
export const applicationStatusSubscriptionsStateForOrgIdSelector = createSelector(
  [applicationStatusSubscriptionsStateByOrgIdSelector, (_state: State, orgId: number) => orgId],
  (applicationStatusSubscriptionsStateByOrgId, orgId) =>
    applicationStatusSubscriptionsStateByOrgId[orgId],
);
